/**
 * Active Insights panel: groups insights by category (risk, deadline, improvement, reputation),
 * shows title, description, severity, and CTA. Resolved insights are hidden by default
 * (API returns activeOnly=true by default).
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody } from '../ui';
import { AlertTriangle, Calendar, TrendingUp, Award, ChevronRight } from 'lucide-react';
import { acknowledgeInsight } from '../../api/analytics';
import { formatAnalyticsDocumentDisplay } from '../../utils/analyticsDocumentLabel';

const CATEGORIES = {
  risk: { label: 'Risk', icon: AlertTriangle, route: '/documents' },
  deadline: { label: 'Deadlines', icon: Calendar, route: '/timeline' },
  improvement: { label: 'Improvements', icon: TrendingUp, route: '/dashboard' },
  reputation: { label: 'Wins & reputation', icon: Award, route: '/passport' },
};

function categorizeInsight(insight) {
  if (insight.category) return insight.category;
  const type = (insight.insightType || '').toLowerCase();
  if (
    type.includes('expir') ||
    type.includes('expired') ||
    type.includes('cluster') ||
    type.includes('hygiene') ||
    type.includes('missing')
  )
    return 'risk';
  if (type.includes('due') || type.includes('registration') || type.includes('mcs150')) return 'deadline';
  if (
    type.includes('improved') ||
    type.includes('current') ||
    type.includes('streak') ||
    type.includes('clean')
  )
    return 'improvement';
  return 'reputation';
}

export default function ActiveInsightsPanel({ insights = [], loading, onAcknowledge }) {
  const navigate = useNavigate();
  const grouped = useMemo(() => {
    const groups = { risk: [], deadline: [], improvement: [], reputation: [] };
    (insights || []).forEach((i) => {
      const cat = categorizeInsight(i);
      groups[cat].push(i);
    });
    return groups;
  }, [insights]);

  if (loading) {
    return (
      <Card className="analytics-card active-insights-panel">
        <CardHeader title="Active insights" subtitle="What needs your attention" />
        <CardBody>
          <div className="analytics-card-loading">
            <div className="loading-spinner" />
            <span>Loading insights...</span>
          </div>
        </CardBody>
      </Card>
    );
  }

  const total = insights?.length || 0;
  if (total === 0) {
    return (
      <Card className="analytics-card active-insights-panel">
        <CardHeader title="Active insights" subtitle="What needs your attention" />
        <CardBody>
          <p className="analytics-empty-message">No active insights right now. You're in good shape.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="analytics-card active-insights-panel">
      <CardHeader title="Active insights" subtitle={`${total} item${total === 1 ? '' : 's'} for you`} />
      <CardBody>
        {Object.entries(CATEGORIES).map(([key, { label, icon: Icon, route }]) => {
          const items = grouped[key] || [];
          if (items.length === 0) return null;
          return (
            <div key={key} className="insights-category">
              <h4 className="insights-category-title">
                <Icon size={18} />
                {label}
              </h4>
              <ul className="insights-list">
                {items.map((i) => (
                  <li key={i.id} className={`insight-item severity-${i.severity || 'medium'}`}>
                    <div className="insight-item-main">
                      <span className="insight-item-title">{formatAnalyticsDocumentDisplay(i.title)}</span>
                      {i.description && (
                        <span className="insight-item-desc">{formatAnalyticsDocumentDisplay(i.description)}</span>
                      )}
                    </div>
                    <div className="insight-item-actions">
                      <button
                        type="button"
                        className="insight-cta"
                        onClick={() => {
                          navigate(route);
                          if (onAcknowledge) onAcknowledge(i.id);
                          else acknowledgeInsight(i.id).catch(() => {});
                        }}
                      >
                        View
                        <ChevronRight size={16} />
                      </button>
                      <button
                        type="button"
                        className="insight-dismiss"
                        onClick={() => {
                          acknowledgeInsight(i.id).then(() => onAcknowledge && onAcknowledge(i.id)).catch(() => {});
                        }}
                        aria-label="Dismiss"
                      >
                        Got it
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}
